import { Link } from "react-router-dom";
import { ArrowRight, Check, Building2, Users, Calendar, Download, Award } from "lucide-react";
import Reveal from "@/components/site/Reveal";
import SectionCTA from "@/components/site/SectionCTA";
import { CONTACT, IMG } from "@/lib/site";
import useSeo from "@/lib/useSeo";
import { useT } from "@/lib/i18n";

const Asociaciones = () => {
  const t = useT();
  useSeo({
    title: {
      es: "Para Asociaciones, Hospitales y Fundaciones · Método TAO",
      ca: "Per a Associacions, Hospitals i Fundacions · Mètode TAO",
    },
    description: {
      es: "Programas de yoga oncológico y acompañamiento cuerpo-mente para asociaciones de pacientes, hospitales y fundaciones. Talleres, programas grupales, formación a equipos sanitarios. Colaboración activa con Hospital Clínic de Barcelona.",
      ca: "Programes de ioga oncològic i acompanyament cos-ment per a associacions de pacients, hospitals i fundacions. Tallers, programes grupals, formació a equips sanitaris. Col·laboració activa amb l'Hospital Clínic de Barcelona.",
    },
    path: "/asociaciones",
  });

  const formats = [
    { icon: Calendar, title: t("asoc.format1.title"), body: t("asoc.format1.body") },
    { icon: Users, title: t("asoc.format2.title"), body: t("asoc.format2.body") },
    { icon: Award, title: t("asoc.format3.title"), body: t("asoc.format3.body") },
    { icon: Building2, title: t("asoc.format4.title"), body: t("asoc.format4.body") },
  ];

  const credentialsT = [
    t("asoc.creds.1"),
    t("asoc.creds.2"),
    t("asoc.creds.3"),
    t("asoc.creds.4"),
    t("asoc.creds.5"),
  ];

  return (
    <div className="bg-ivory">
      {/* HERO */}
      <section className="pt-12 md:pt-20 pb-16 md:pb-20" data-testid="page-asociaciones">
        <div className="max-w-[1300px] mx-auto px-6 md:px-12 lg:px-16 grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <Reveal className="lg:col-span-7">
            <p className="text-[11px] tracking-[0.32em] uppercase text-sage mb-6">
              {t("asoc.eyebrow")}
            </p>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-charcoal leading-[1.02]">
              {t("asoc.title1")} <span className="italic text-terracotta">{t("asoc.title2")}</span>.
            </h1>
            <p className="mt-7 text-lg md:text-xl leading-relaxed text-charcoal/75 font-light max-w-xl">
              {t("asoc.lead")}
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-3.5">
              <Link
                to="/contacto?interest=asociacion"
                data-testid="asoc-cta-contact"
                className="inline-flex items-center justify-center gap-2 bg-charcoal text-ivory rounded-full px-7 py-4 text-sm font-medium hover:bg-terracotta transition-all group"
              >
                {t("asoc.contactBtn")} <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <a
                href="/dossier-asociaciones.html"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="asoc-cta-dossier"
                className="inline-flex items-center justify-center gap-2 border border-charcoal/30 text-charcoal rounded-full px-7 py-4 text-sm font-medium hover:bg-charcoal hover:text-ivory transition-all"
              >
                <Download size={16} /> {t("asoc.dossierBtn")}
              </a>
            </div>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-5">
            <div className="aspect-[4/5] rounded-tr-[4rem] rounded-bl-[4rem] overflow-hidden bg-sand">
              <img
                src={IMG.hospitalSavasana}
                alt="Sesión grupal de yoga oncológico"
                className="w-full h-full object-cover"
                fetchPriority="high"
                decoding="async"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* TRUST BAND */}
      <section className="py-12 border-y border-sand/60 bg-cream/40">
        <div className="max-w-[1100px] mx-auto px-6 md:px-12 text-center">
          <p className="text-[11px] tracking-[0.32em] uppercase text-sage mb-5">
            {t("asoc.trustBand.eyebrow")}
          </p>
          <p className="font-serif italic text-2xl md:text-3xl text-charcoal leading-[1.35] max-w-3xl mx-auto">
            "{t("asoc.trustBand.lead1")} <span className="text-terracotta not-italic font-medium">{t("asoc.trustBand.lead2")}</span>{t("asoc.trustBand.lead3")}"
          </p>
        </div>
      </section>

      {/* FORMATS */}
      <section className="py-20 md:py-28" data-testid="asoc-formats">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <Reveal className="max-w-2xl mb-14">
            <p className="text-[11px] tracking-[0.32em] uppercase text-terracotta mb-5">
              {t("asoc.formatsEyebrow")}
            </p>
            <h2 className="font-serif text-4xl md:text-5xl text-charcoal leading-[1.05]">
              {t("asoc.formatsTitle1")} <span className="italic">{t("asoc.formatsTitle2")}</span>.
            </h2>
            <p className="mt-5 text-charcoal/75 font-light leading-relaxed">
              {t("asoc.formatsLead")}
            </p>
          </Reveal>

          <div className="grid sm:grid-cols-2 gap-6 lg:gap-8">
            {formats.map((f, i) => (
              <Reveal key={f.title} delay={i * 0.08}>
                <article className="bg-white rounded-3xl p-8 border border-sand/60 h-full" data-testid={`format-${i}`}>
                  <span className="w-12 h-12 rounded-full bg-terracotta/15 flex items-center justify-center text-terracotta">
                    <f.icon size={20} />
                  </span>
                  <h3 className="mt-6 font-serif text-2xl text-charcoal leading-snug">{f.title}</h3>
                  <p className="mt-3 text-charcoal/70 leading-relaxed text-[15px]">{f.body}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="py-20 md:py-28 bg-charcoal text-ivory">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <Reveal className="lg:col-span-6">
            <p className="text-[11px] tracking-[0.32em] uppercase text-terracotta mb-5">
              {t("asoc.why.eyebrow")}
            </p>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05]">
              {t("asoc.why.title1")} <span className="italic text-terracotta">{t("asoc.why.title2")}</span>{t("asoc.why.title3")}
            </h2>
            <p className="mt-6 text-ivory/75 leading-relaxed font-light text-lg">
              {t("asoc.why.body")}
            </p>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-6">
            <ul className="space-y-4">
              {credentialsT.map((c, i) => (
                <li key={c} className="flex items-start gap-4" data-testid={`credential-${i}`}>
                  <Check size={18} className="text-terracotta shrink-0 mt-1" />
                  <span className="text-ivory/85 text-[15.5px] leading-relaxed">{c}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* DOSSIER CTA */}
      <section className="py-20 md:py-28" data-testid="asoc-dossier-cta">
        <div className="max-w-[1100px] mx-auto px-6 md:px-12">
          <Reveal>
            <div className="rounded-3xl bg-gradient-to-br from-cream/60 to-sand/30 p-10 md:p-14 border border-sand/50 grid md:grid-cols-[1fr_auto] gap-10 items-center">
              <div>
                <p className="text-[11px] tracking-[0.32em] uppercase text-terracotta mb-4">
                  {t("asoc.dossier.eyebrow")}
                </p>
                <h2 className="font-serif text-3xl md:text-4xl text-charcoal leading-[1.1]">
                  {t("asoc.dossier.title1")} <span className="italic">{t("asoc.dossier.title2")}</span>.
                </h2>
                <p className="mt-4 text-charcoal/70 leading-relaxed">
                  {t("asoc.dossier.body")}
                </p>
              </div>
              <a
                href="/dossier-asociaciones.html"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="dossier-download-btn"
                className="inline-flex items-center justify-center gap-2 bg-charcoal text-ivory rounded-full px-8 py-5 text-sm font-medium hover:bg-terracotta transition-all shrink-0"
              >
                <Download size={16} /> {t("asoc.dossier.btn")}
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CONTACT CTA */}
      <SectionCTA
        title={t("asoc.contactCta.title")}
        body={`${t("asoc.contactCta.body")} ${CONTACT.email}${t("asoc.contactCta.body2")}`}
        primaryLabel={t("asoc.contactCta.btn")}
        primaryTo={`mailto:${CONTACT.email}?subject=Colaboraci%C3%B3n%20asociaci%C3%B3n%20%2F%20entidad`}
      />
    </div>
  );
};

export default Asociaciones;
